'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Send, CheckCircle, AlertCircle, User, Mail, Phone, Building2, Code,
  MapPin, ArrowRight, ArrowLeft, Check,
} from 'lucide-react';
import DynamicCompanyInfo from '@/components/DynamicCompanyInfo';

/* ----------------------------- validation ----------------------------- */

const schema = z.object({
  name: z.string().min(2, 'Please enter your full name'),
  email: z.string().email('Enter a valid email address'),
  phone: z
    .string()
    .optional()
    .refine((v) => !v || /^[+()\-\d\s]{7,20}$/.test(v), 'Enter a valid phone number'),
  company: z.string().optional(),
  projectType: z.string().min(1, 'Select a project type'),
  budget: z.string().min(1, 'Select a budget range'),
  timeline: z.string().min(1, 'Select a timeline'),
  description: z.string().min(20, 'Please describe your project (at least 20 characters)'),
});

type FormValues = z.infer<typeof schema>;

const projectTypes = [
  'Web Development', 'Mobile App Development', 'E-commerce Development', 'AI/ML Services',
  'Blockchain Development', 'DevOps & Cloud', 'IoT & Embedded Systems', 'Cybersecurity',
  'Custom Software', 'Consultation', 'Other',
];
const budgetRanges = [
  'Under $5,000', '$5,000 - $15,000', '$15,000 - $50,000', '$50,000 - $100,000',
  'Over $100,000', 'Not Sure',
];
const timelineOptions = ['ASAP', '1-3 months', '3-6 months', '6-12 months', 'Over 1 year', 'Flexible'];

const steps = [
  { id: 1, title: 'About You', icon: User, fields: ['name', 'email', 'phone', 'company'] as const },
  { id: 2, title: 'Your Project', icon: Code, fields: ['projectType', 'budget', 'timeline'] as const },
  { id: 3, title: 'Details', icon: Send, fields: ['description'] as const },
];

/* --------------------------- small UI atoms ---------------------------- */

function Field({
  label, required, error, children,
}: { label: string; required?: boolean; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      <AnimatePresence>
        {error && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="text-xs text-red-500 mt-1 flex items-center gap-1"
          >
            <AlertCircle className="w-3.5 h-3.5" /> {error}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

const inputCls =
  'w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors';

/* ------------------------------ component ------------------------------ */

export default function ProjectInquiryForm() {
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [serverError, setServerError] = useState('');

  const {
    register, handleSubmit, trigger, reset, watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      name: '', email: '', phone: '', company: '',
      projectType: '', budget: '', timeline: '', description: '',
    },
  });

  const descLen = watch('description')?.length || 0;

  const next = async () => {
    const valid = await trigger(steps[step].fields as unknown as (keyof FormValues)[]);
    if (valid) setStep((s) => Math.min(s + 1, steps.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    setServerError('');
    try {
      const res = await fetch('/api/project-inquiries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to submit inquiry');
      }
      setIsSubmitted(true);
    } catch (e) {
      setServerError(e instanceof Error ? e.message : 'Failed to submit inquiry');
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ----------------------------- success ----------------------------- */
  if (isSubmitted) {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl p-10 text-center max-w-xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
              className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle className="w-11 h-11 text-green-500" />
            </motion.div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Thank You!</h3>
            <p className="text-gray-600 mb-6">
              We've received your project inquiry and will get back to you within 24 hours.
            </p>
            <button
              onClick={() => { setIsSubmitted(false); setStep(0); reset(); }}
              className="btn-primary"
            >
              Submit Another Inquiry
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  /* ------------------------------- form ------------------------------- */
  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-100 py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ready to Start Your Next Project?
          </h2>
          <p className="text-lg sm:text-xl text-gray-600">
            Tell us about your project and we'll provide a custom solution just for you
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              {/* Stepper */}
              <div className="px-6 sm:px-8 pt-8">
                <div className="flex items-center justify-between">
                  {steps.map((s, i) => {
                    const Icon = s.icon;
                    const done = i < step;
                    const active = i === step;
                    return (
                      <div key={s.id} className="flex items-center flex-1 last:flex-none">
                        <div className="flex flex-col items-center">
                          <motion.div
                            animate={{
                              backgroundColor: done || active ? '#2563eb' : '#e5e7eb',
                              scale: active ? 1.1 : 1,
                            }}
                            className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                          >
                            {done ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                          </motion.div>
                          <span className={`text-xs mt-2 font-medium ${active ? 'text-blue-600' : 'text-gray-400'}`}>
                            {s.title}
                          </span>
                        </div>
                        {i < steps.length - 1 && (
                          <div className="flex-1 h-1 mx-2 rounded bg-gray-200 overflow-hidden">
                            <motion.div
                              className="h-full bg-blue-600"
                              initial={false}
                              animate={{ width: i < step ? '100%' : '0%' }}
                              transition={{ duration: 0.4 }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="p-6 sm:p-8">
                {serverError && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                    <AlertCircle className="w-5 h-5" /> <span>{serverError}</span>
                  </div>
                )}

                <AnimatePresence mode="wait">
                  {/* Step 1 */}
                  {step === 0 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      className="grid grid-cols-1 md:grid-cols-2 gap-5"
                    >
                      <Field label="Full Name" required error={errors.name?.message}>
                        <input {...register('name')} className={`${inputCls} ${errors.name ? 'border-red-300' : 'border-gray-300'}`} placeholder="John Doe" />
                      </Field>
                      <Field label="Email Address" required error={errors.email?.message}>
                        <input {...register('email')} className={`${inputCls} ${errors.email ? 'border-red-300' : 'border-gray-300'}`} placeholder="john@example.com" />
                      </Field>
                      <Field label="Phone Number" error={errors.phone?.message}>
                        <input {...register('phone')} className={`${inputCls} ${errors.phone ? 'border-red-300' : 'border-gray-300'}`} placeholder="+1 (555) 123-4567" />
                      </Field>
                      <Field label="Company Name" error={errors.company?.message}>
                        <input {...register('company')} className={`${inputCls} border-gray-300`} placeholder="Acme Corporation" />
                      </Field>
                    </motion.div>
                  )}

                  {/* Step 2 */}
                  {step === 1 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      className="grid grid-cols-1 md:grid-cols-3 gap-5"
                    >
                      <Field label="Project Type" required error={errors.projectType?.message}>
                        <select {...register('projectType')} className={`${inputCls} ${errors.projectType ? 'border-red-300' : 'border-gray-300'}`}>
                          <option value="">Select type</option>
                          {projectTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </Field>
                      <Field label="Budget Range" required error={errors.budget?.message}>
                        <select {...register('budget')} className={`${inputCls} ${errors.budget ? 'border-red-300' : 'border-gray-300'}`}>
                          <option value="">Select budget</option>
                          {budgetRanges.map((b) => <option key={b} value={b}>{b}</option>)}
                        </select>
                      </Field>
                      <Field label="Timeline" required error={errors.timeline?.message}>
                        <select {...register('timeline')} className={`${inputCls} ${errors.timeline ? 'border-red-300' : 'border-gray-300'}`}>
                          <option value="">Select timeline</option>
                          {timelineOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </Field>
                    </motion.div>
                  )}

                  {/* Step 3 */}
                  {step === 2 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                    >
                      <Field label="Project Description" required error={errors.description?.message}>
                        <textarea
                          {...register('description')}
                          rows={6}
                          className={`${inputCls} ${errors.description ? 'border-red-300' : 'border-gray-300'}`}
                          placeholder="Tell us about your project, goals, requirements, and any specific features you need..."
                        />
                        <div className="text-right text-xs text-gray-400 mt-1">{descLen} characters</div>
                      </Field>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Nav buttons */}
                <div className="mt-8 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={back}
                    disabled={step === 0}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-gray-300 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>

                  {step < steps.length - 1 ? (
                    <button
                      type="button"
                      onClick={next}
                      className="btn-primary inline-flex items-center gap-2"
                    >
                      Continue <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" /> Submit Inquiry
                        </>
                      )}
                    </button>
                  )}
                </div>

                <p className="mt-4 text-center text-sm text-gray-500">
                  We'll get back to you within 24 hours with a detailed proposal
                </p>
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <Building2 className="w-5 h-5 mr-2 text-blue-600" /> Get In Touch
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Email us</p>
                    <DynamicCompanyInfo type="email">
                      {(companyInfo) => (
                        <a href={`mailto:${companyInfo?.contact?.email || 'hello@company.com'}`} className="text-gray-900 hover:text-blue-600 transition-colors font-medium">
                          {companyInfo?.contact?.email || 'hello@company.com'}
                        </a>
                      )}
                    </DynamicCompanyInfo>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Phone className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Call us</p>
                    <DynamicCompanyInfo type="phone">
                      {(companyInfo) => (
                        <a href={`tel:${companyInfo?.contact?.phone || '+1 (555) 123-4567'}`} className="text-gray-900 hover:text-blue-600 transition-colors font-medium">
                          {companyInfo?.contact?.phone || '+1 (555) 123-4567'}
                        </a>
                      )}
                    </DynamicCompanyInfo>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Visit us</p>
                    <DynamicCompanyInfo type="address">
                      {(companyInfo) => (
                        <p className="text-gray-900">
                          {companyInfo?.address?.fullAddress || '123 Tech Street, San Francisco, CA 94105'}
                        </p>
                      )}
                    </DynamicCompanyInfo>
                  </div>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-600"><strong>Response Time:</strong> Within 24 hours</p>
                <p className="text-sm text-gray-600 mt-1"><strong>Business Hours:</strong> Mon-Fri 9AM-6PM PST</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Why Choose Us?</h3>
              <div className="space-y-4">
                {[
                  { t: 'Expert Team', d: 'Skilled developers with 10+ years experience' },
                  { t: 'On-Time Delivery', d: '98% projects delivered on schedule' },
                  { t: '24/7 Support', d: 'Ongoing support and maintenance included' },
                  { t: 'Quality Assured', d: 'Rigorous testing and quality control' },
                ].map((item) => (
                  <div key={item.t} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">{item.t}</h4>
                      <p className="text-sm text-gray-600">{item.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
